/**
 * /admin/usage — platform-admin pilot/usage dashboard.
 *
 * Who's using Ledger Core: accounts + how much data each holds, who has logged
 * in and when, and what pages they've visited. Platform-admin only (the route
 * also renders a friendly notice for non-admins; the API enforces the gate).
 */
import { createFileRoute } from "@tanstack/react-router";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  useAccountsUsage,
  usePageviews,
  useUsersUsage,
} from "@/lib/adminUsage";
import { useAuth } from "@/auth/store";

export const Route = createFileRoute("/_auth/admin/usage")({
  component: UsagePage,
});

function fmt(ts: string | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function profileLabel(value: string | null): string {
  if (!value) return "Any industry";
  return value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>{title}</Typography>
      {children}
    </Paper>
  );
}

function UsagePage() {
  const user = useAuth((s) => s.user);
  const accounts = useAccountsUsage();
  const users = useUsersUsage();
  const views = usePageviews(200);

  if (user && user.is_platform_admin === false) {
    return (
      <Box sx={{ p: 3, maxWidth: 700, mx: "auto" }}>
        <Alert severity="info">This page is for Bynaus platform admins.</Alert>
      </Box>
    );
  }

  const countKeys = accounts.data?.length
    ? Object.keys(accounts.data[0].record_counts)
    : [];
  const reviewers = users.data?.filter((u) => u.signup_purpose === "demo_reviewer") ?? [];
  const teamUsers = users.data?.filter((u) => u.signup_purpose !== "demo_reviewer") ?? [];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
        Usage
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Who's using Ledger Core, what they're looking at, and how much data each
        account holds.
      </Typography>

      <Section title="Private demo reviewers">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Invitation-only prospects. Their sample companies are isolated from live books;
          activity is recorded only to preserve progress and understand product review.
        </Typography>
        {users.isLoading ? <CircularProgress size={22} /> : reviewers.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reviewer</TableCell>
                <TableCell>Company / role</TableCell>
                <TableCell>Demo requested</TableCell>
                <TableCell>Sample workspace</TableCell>
                <TableCell>Latest activity</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviewers.map((reviewer) => {
                const testing = reviewer.demo_accounts.length > 0 || reviewer.pageview_count > 0;
                const signedIn = !!reviewer.last_login_at;
                return (
                  <TableRow key={reviewer.user_id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {reviewer.name || reviewer.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {reviewer.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {reviewer.organization_name || "—"}
                      </Typography>
                      {reviewer.job_title && (
                        <Typography variant="caption" color="text.secondary">
                          {reviewer.job_title}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{profileLabel(reviewer.demo_interest)}</TableCell>
                    <TableCell>
                      {reviewer.demo_accounts.join(", ") || "Not created yet"}
                      {reviewer.demo_profiles.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {reviewer.demo_profiles.map(profileLabel).join(", ")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {fmt(reviewer.last_activity_at || reviewer.last_login_at)}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {reviewer.pageview_count} tracked page{reviewer.pageview_count === 1 ? "" : "s"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={testing ? "Testing" : signedIn ? "Signed in" : "Invited"}
                        size="small"
                        color={testing ? "success" : signedIn ? "primary" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No private reviewers have been invited yet. Send one from Setup → Team.
          </Typography>
        )}
      </Section>

      <Section title="Accounts">
        {accounts.isLoading ? <CircularProgress size={22} /> : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell align="right">Users</TableCell>
                {countKeys.map((k) => (
                  <TableCell key={k} align="right">{k.replace(/_/g, " ")}</TableCell>
                ))}
                <TableCell align="right">Total records</TableCell>
                <TableCell>Last login</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.data?.map((a) => (
                <TableRow key={a.entity_id} hover>
                  <TableCell>
                    {a.name}{" "}
                    {a.is_demo && (
                      <Chip
                        label={`Demo · ${profileLabel(a.demo_profile)}`}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ mr: 0.75 }}
                      />
                    )}
                    <Typography component="span" variant="caption" color="text.secondary">
                      ({a.code})
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{a.user_count}</TableCell>
                  {countKeys.map((k) => (
                    <TableCell key={k} align="right">{a.record_counts[k] ?? 0}</TableCell>
                  ))}
                  <TableCell align="right"><strong>{a.total_records}</strong></TableCell>
                  <TableCell>{fmt(a.last_login_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section title="Users">
        {users.isLoading ? <CircularProgress size={22} /> : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Account(s)</TableCell>
                <TableCell>Last login</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teamUsers.map((u) => (
                <TableRow key={u.user_id} hover>
                  <TableCell>
                    {u.email}
                    {u.is_platform_admin && (
                      <Chip label="Bynaus" size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{u.accounts.join(", ") || "—"}</TableCell>
                  <TableCell>{fmt(u.last_login_at)}</TableCell>
                  <TableCell>
                    {u.is_active
                      ? <Chip label="active" size="small" color="success" variant="outlined" />
                      : <Chip label="inactive" size="small" />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section title="Recent page views">
        {views.isLoading ? <CircularProgress size={22} /> : views.data?.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Page</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {views.data.map((v, i) => (
                <TableRow key={i} hover>
                  <TableCell>{fmt(v.at)}</TableCell>
                  <TableCell>{v.email}</TableCell>
                  <TableCell>{v.account ?? "—"}</TableCell>
                  <TableCell><code>{v.path}</code></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No page views recorded yet.
          </Typography>
        )}
      </Section>
    </Box>
  );
}
